/**
 * service-dossiers-en.authored-c.ts — Wave 164
 *
 * Third batch of hand-authored English case dossiers (no machine translation,
 * no AI credits required at runtime or build time).
 *
 * Rules enforced here:
 *  - no pricing anywhere (meta or body)
 *  - no Review / AggregateRating / ClaimReview data
 *  - no guarantees; every duration is a working-day range with a caveat
 *  - Apostille wording stays conditional: Thailand deposited its instrument of
 *    accession on 30 June 2026 and the Convention enters into force for
 *    Thailand on 28 February 2027; until then the consular legalisation chain
 *    applies.
 */
import type { ServiceDossier } from "./service-dossiers";

const MFA_CITE = {
  label: "Department of Consular Affairs, Ministry of Foreign Affairs (Thailand)",
  url: "https://consular.mfa.go.th/",
};
const DBD_CITE = { label: "Department of Business Development (DBD)", url: "https://www.dbd.go.th/" };
const DOPA_CITE = {
  label: "Department of Provincial Administration (District Registrars)",
  url: "https://www.dopa.go.th/",
};
const IMMI_CITE = { label: "Immigration Bureau (Thailand)", url: "https://www.immigration.go.th/" };
const BOI_CITE = { label: "Thailand Board of Investment — Long-Term Resident visa", url: "https://ltr.boi.go.th/" };
const RD_CITE = { label: "The Revenue Department (Thailand)", url: "https://www.rd.go.th/" };
const COURT_CITE = { label: "Courts of Justice (Thailand)", url: "https://www.coj.go.th/" };

const CASES_LINK: [string, string] = ["All case studies", "/en/case-studies"];

export const EN_AUTHORED_DOSSIERS_C: ServiceDossier[] = [
  {
    slug: "company-registration-foreign-director",
    cluster: "company",
    title: "Registering a Thai company with a foreign director",
    metaTitle: "Case study: Thai company registration with a foreign director",
    metaDescription:
      "How a Thai limited company with a foreign director is actually registered: name reservation, promoters, capital, notarised foreign identity documents, DBD filing, tax registration and the mistakes that force a refiling.",
    h1: "Case study: registering a Thai limited company when a director lives abroad",
    answer:
      "A Thai limited company is registered with the Department of Business Development after the name is reserved, the memorandum of association is filed and the statutory meeting is held. When a director or shareholder is a foreign national living abroad, their passport copy and signature must be notarised in their country of residence and, where required, legalised before the Thai registrar will accept the signed forms. Most files complete in one to three weeks once the foreign paperwork arrives in the correct form.",
    audience:
      "Foreign founders, overseas parent companies and Thai partners setting up a limited company where at least one director signs from outside Thailand.",
    facts: [
      { label: "Registering authority", value: "Department of Business Development, Ministry of Commerce" },
      { label: "Minimum shareholders", value: "Two or more promoters for a private limited company" },
      { label: "Foreign signatory documents", value: "Passport copy and signature certified abroad, then legalised where the registrar requires it" },
      { label: "Language of filing", value: "Thai; foreign documents are filed with certified Thai translations" },
      { label: "Typical duration", value: "1–3 weeks after all foreign-signed documents are received in acceptable form" },
    ],
    documents: [
      "Three proposed company names in order of preference for the reservation check",
      "Passport copies of every foreign promoter, director and shareholder, certified abroad",
      "Signature specimens of foreign directors, notarised in the country of signing",
      "Proof of the registered office address with the owner's consent letter and house registration",
      "Shareholder list with the capital split and share value",
      "Objectives of the company, drafted to cover the activities actually intended",
      "Certified Thai translations of every foreign-language document in the bundle",
    ],
    steps: [
      {
        title: "Screen the business activity first",
        detail:
          "We check whether the intended activity is restricted for foreign shareholding and whether a licence, a treaty route or a promotion route is the sensible structure before any name is reserved.",
        who: "Advisory team",
        eta: "1–3 working days",
      },
      {
        title: "Reserve the company name",
        detail:
          "Names are checked against the registrar's rules on similarity and reserved words; three options avoid a second round.",
        who: "Department of Business Development",
        eta: "1–3 working days",
      },
      {
        title: "Certify the foreign director's documents abroad",
        detail:
          "The passport copy and signature are certified before a notary in the country of residence, then legalised where the registrar asks for it. Sending the exact wording in advance prevents a second trip.",
        who: "Notary abroad and the relevant mission",
        eta: "3–15 working days depending on the country",
      },
      {
        title: "File the memorandum and hold the statutory meeting",
        detail:
          "The memorandum, shareholder list, directors and authorised-signature clause are prepared and signed, then the statutory meeting minutes are recorded.",
        who: "Promoters",
        eta: "1–3 working days",
      },
      {
        title: "Register the company and complete tax registration",
        detail:
          "The registration is lodged with the Department of Business Development, followed by the taxpayer identification and VAT registration where the activity or turnover requires it.",
        who: "DBD and the Revenue Department",
        eta: "3–10 working days",
      },
    ],
    pitfalls: [
      "A foreign passport copy certified only by a bank or an employer — the registrar expects a notarial act.",
      "Name spellings that differ between the passport, the notarial certificate and the Thai translation.",
      "Objectives drafted too narrowly, forcing an amendment filing before the first contract can be signed.",
      "A registered office consent letter missing the owner's identification or house registration.",
      "Signing foreign documents before the Thai text is finalised, so the whole set has to be re-signed.",
      "Assuming a foreign majority is possible for an activity that is in fact restricted.",
    ],
    compare: [
      {
        option: "Thai majority private limited company",
        useWhen: "The activity is restricted and no promotion or treaty route applies",
        time: "1–3 weeks",
        note: "Fastest common structure; shareholder arrangements must be genuine",
      },
      {
        option: "Foreign business licence route",
        useWhen: "Foreign majority is needed in a restricted activity",
        time: "Several months",
        note: "Substantive review, so plan contract dates around it",
      },
      {
        option: "Branch or representative office",
        useWhen: "A foreign parent wants presence without a separate Thai entity",
        time: "Varies by activity",
        note: "Activity scope is narrower than a limited company",
      },
    ],
    faqs: [
      {
        q: "Does the foreign director have to fly to Thailand to register the company?",
        a: "Usually not. The signature and passport copy can be certified before a notary where the director lives and couriered to Thailand. What matters is that the certification wording, the document order and the name spelling match what the registrar expects, which is why we send the exact text to be signed before the appointment abroad.",
      },
      {
        q: "How many shareholders does a Thai limited company need?",
        a: "A private limited company is formed by two or more promoters who become the first shareholders. The split matters more than the count: the shareholding structure decides whether the company is treated as Thai or foreign for the activity you intend, so we map that before the memorandum is drafted.",
      },
      {
        q: "Do foreign documents need Thai translations?",
        a: "Yes. Filings at the Department of Business Development are in Thai, so foreign-language passports, notarial certificates and corporate documents are filed with certified Thai translations. The translation has to reproduce names exactly as the passport shows them, because the registrar compares them line by line.",
      },
      {
        q: "Can the company start operating immediately after registration?",
        a: "Registration creates the legal entity, but before trading you normally need a corporate bank account, taxpayer registration, VAT registration where relevant, and a work permit for any foreign national who will work here. We usually sequence those in parallel so the first month is not lost waiting.",
      },
      {
        q: "What happens if the objectives are wrong?",
        a: "They can be amended, but an amendment is a fresh filing with its own meeting minutes and waiting time, and banks sometimes pause account opening while it is pending. Drafting the objectives around the activities you realistically expect in the first two years avoids that entirely.",
      },
      {
        q: "Can you handle the entire registration for us?",
        a: "Yes. We advise on the structure, reserve the name, prepare and translate the whole bundle, coordinate the notarial step abroad, file with the registrar and complete tax registration. With more than 15 years of case work we treat this as advisory rather than form filling — the structure decision at the start is what saves months later.",
      },
    ],
    related: [
      ["Company setup service", "/en/company"],
      ["Notary Public service", "/en/notary"],
      ["Certified translation", "/en/services/translation"],
      CASES_LINK,
    ],
    citations: [DBD_CITE, RD_CITE],
  },

  {
    slug: "name-change-document-chain",
    cluster: "translate",
    title: "A name change and the document chain that follows",
    metaTitle: "Case study: Thai name change and the document chain",
    metaDescription:
      "After a legal name change, every certificate, degree and bank record has to line up. The evidence chain, translation rules, legalisation order and the mismatches that cause overseas rejections.",
    h1: "Case study: keeping every document consistent after a legal name change",
    answer:
      "When a name changes in Thailand, the district registrar issues a name change certificate, but older certificates keep the previous name. Overseas authorities accept that only when the name change certificate is presented alongside them, translated and legalised in the same chain. The practical work is building an evidence bridge from the birth certificate to the current passport so no reader has to guess that two names are one person.",
    audience:
      "Anyone who changed a first name, surname or married name in Thailand and now has to submit documents abroad, apply for a visa, or claim a foreign entitlement.",
    facts: [
      { label: "Issuing authority", value: "District registrar, Department of Provincial Administration" },
      { label: "Core evidence", value: "Name change certificate, or marriage or divorce certificate for a married-name change" },
      { label: "Translation rule", value: "Every document in the chain is translated by the same standard and spelling convention" },
      { label: "Legalisation", value: "The certificate travels through the same chain as the document it supports" },
      { label: "Typical duration", value: "5–15 working days for a full chain, depending on how many certificates are involved" },
    ],
    documents: [
      "Name change certificate issued by the district registrar",
      "Marriage or divorce certificate where the change followed marital status",
      "Current passport and Thai identification card",
      "House registration showing the recorded name",
      "The older certificates still bearing the previous name — birth certificate, degree, transcript, employment records",
      "Certified translations of every item above, with a consistent transliteration",
    ],
    steps: [
      {
        title: "List every document the receiver will see",
        detail:
          "We build the list from the receiving authority's checklist, not from what the client happens to hold, so nothing surfaces late in the process.",
        who: "Advisory team",
        eta: "Same day",
      },
      {
        title: "Fix one transliteration and apply it everywhere",
        detail:
          "The passport spelling is the anchor. Every translation in the bundle uses it, including translations of documents that predate the change.",
        who: "Translation team",
        eta: "1–2 working days",
      },
      {
        title: "Re-issue what can be re-issued",
        detail:
          "Some certificates can be reissued in the new name; others cannot and must travel with the name change certificate. We separate the two before ordering anything.",
        who: "Issuing authorities",
        eta: "1–7 working days",
      },
      {
        title: "Translate and legalise the chain together",
        detail:
          "The name change certificate is lodged with each supporting document so the certification covers the link, not just the individual certificate.",
        who: "Department of Consular Affairs",
        eta: "3–7 working days",
      },
      {
        title: "Add the destination embassy layer where required",
        detail:
          "Where the destination still requires its own consular act, the completed set is lodged with its mission in Thailand.",
        who: "Destination embassy",
        eta: "1–10 working days",
      },
    ],
    pitfalls: [
      "Submitting a degree in the previous name without the name change certificate attached.",
      "Two different English spellings of the same Thai surname across the bundle.",
      "Translating only the new documents and leaving the older ones untranslated.",
      "Legalising the certificates separately, so the reader cannot see they belong to one file.",
      "Forgetting bank, insurance and property records that the receiving authority will cross-check.",
      "Assuming a married-name change is automatic abroad — most authorities want the marriage certificate itself.",
    ],
    compare: [
      {
        option: "Re-issue the certificate in the new name",
        useWhen: "The issuing authority allows reissue, such as some registrar records",
        time: "1–7 working days",
        note: "Cleanest option where available",
      },
      {
        option: "Keep the old certificate plus the name change certificate",
        useWhen: "The original cannot be reissued, such as a degree already conferred",
        time: "Same as a normal legalisation chain",
        note: "Both documents must be translated and legalised together",
      },
      {
        option: "Sworn statement of identity",
        useWhen: "The receiving authority asks for an explicit declaration linking both names",
        time: "1–3 working days",
        note: "Signed before a Notarial Services Attorney and attached to the set",
      },
    ],
    faqs: [
      {
        q: "My degree shows my old name. Will a foreign university reject it?",
        a: "Not if the link is documented. Universities routinely accept a degree in a previous name when the name change certificate is submitted with it, translated and certified in the same chain. Problems arise when the certificate is missing, or when the translation spells the surname differently from the passport.",
      },
      {
        q: "Do I need to translate documents that already predate the change?",
        a: "Yes, if the receiving authority will read them. A partially translated bundle forces the reader to compare Thai script against English text, which is exactly where files get set aside. We translate the whole chain with a single spelling convention so the link is obvious at a glance.",
      },
      {
        q: "Is a marriage certificate enough for a married-name change?",
        a: "Often yes, because it shows the legal event that produced the new name. Some authorities also want the district registrar's name change record where the surname was formally amended in the registry. We confirm which of the two the receiver expects before preparing the set.",
      },
      {
        q: "How long is the whole chain?",
        a: "Five to fifteen working days is typical for a full chain covering several certificates, including translation, consular legalisation and an embassy layer where required. The variable is how many documents have to be re-issued first, which we identify on the first day rather than mid-process.",
      },
      {
        q: "What if my old and new names appear on different visas?",
        a: "That is common and manageable. The immigration authority reads the passport as the anchor, and the name change certificate explains the earlier stamps. Carrying a certified translation of the certificate while travelling avoids questions at the counter.",
      },
      {
        q: "Can you manage the whole chain for me?",
        a: "Yes. We map the documents the receiving authority will actually see, order what can be reissued, translate everything to one convention, and run the legalisation and embassy steps as one file. Fifteen years of these chains has taught us that the fix is planning the order once, not repairing rejections later.",
      },
    ],
    related: [
      ["Certified translation", "/en/services/translation"],
      ["Consular legalisation", "/en/mfa-legalisation"],
      ["Notary Public service", "/en/notary"],
      CASES_LINK,
    ],
    citations: [DOPA_CITE, MFA_CITE],
  },

  {
    slug: "inheritance-estate-documents-abroad",
    cluster: "notary",
    title: "Estate and inheritance documents crossing borders",
    metaTitle: "Case study: Thai inheritance documents used abroad",
    metaDescription:
      "Death certificates, estate powers of attorney, heir declarations and court orders prepared for use overseas — the documents, the notarial and legalisation order, and the errors that stall an estate.",
    h1: "Case study: preparing Thai estate documents that a foreign bank or court will accept",
    answer:
      "Estates that touch two countries need a document bridge: the Thai death certificate and heir evidence for the foreign side, or a foreign grant and power of attorney for the Thai side. Each document is translated, notarised where a signature or a copy has to be certified, and legalised through the consular chain, with the destination embassy layer added where that country requires it. Banks and courts read these files strictly, so the order and the naming consistency matter as much as the content.",
    audience:
      "Heirs, executors, administrators and family members handling an estate where assets, documents or beneficiaries sit in two countries.",
    facts: [
      { label: "Core Thai document", value: "Death certificate issued by the district registrar" },
      { label: "Authority for estate administration", value: "Thai courts appoint an estate administrator where required" },
      { label: "Signature certification", value: "Notarial Services Attorney registered with the Lawyers Council of Thailand" },
      { label: "Legalisation", value: "Department of Consular Affairs, then the destination embassy where required" },
      { label: "Typical duration", value: "2–8 weeks, dominated by the court and bank steps rather than the paperwork" },
    ],
    documents: [
      "Death certificate and, where issued, the removal-from-house-registration record",
      "Evidence of the relationship — birth certificate, marriage certificate, house registration",
      "Will or testamentary document if one exists, with its translation",
      "Court order appointing an estate administrator where the asset holder requires one",
      "Power of attorney allowing a representative to act, signed before a Notarial Services Attorney",
      "Bank statements, share registers, title deeds or policy documents identifying the assets",
      "Passports and identification documents of every heir named in the file",
    ],
    steps: [
      {
        title: "Ask the asset holder what it will accept",
        detail:
          "Banks, insurers and land registries publish different requirements. Getting that list first decides whether a court appointment is needed at all.",
        who: "Advisory team and the asset holder",
        eta: "1–10 days",
      },
      {
        title: "Assemble the evidence of relationship",
        detail:
          "Birth, marriage and house registration records are ordered fresh so their issue dates sit inside the receiver's validity window.",
        who: "District registrar",
        eta: "1–5 working days",
      },
      {
        title: "Obtain the court appointment where required",
        detail:
          "Where an administrator must be appointed, the petition is filed with the competent Thai court and the order is collected once issued.",
        who: "Courts of Justice",
        eta: "Several weeks, depending on the court's calendar",
      },
      {
        title: "Notarise powers of attorney and certified copies",
        detail:
          "Signatures and copies are certified by a Notarial Services Attorney in the wording the foreign institution requires.",
        who: "Notarial Services Attorney",
        eta: "Same day to 2 working days",
      },
      {
        title: "Translate, legalise and deliver",
        detail:
          "The complete set is translated, legalised at the Department of Consular Affairs, certified at the destination embassy where required, and couriered with a tracked reference.",
        who: "Translation team, MFA, destination embassy",
        eta: "5–14 working days",
      },
    ],
    pitfalls: [
      "Sending a plain photocopy of the death certificate to a foreign bank instead of a certified copy with translation.",
      "A power of attorney drafted in general terms when the institution requires the account or asset to be named.",
      "Different spellings of the deceased's name across the death certificate, the will and the bank record.",
      "Starting legalisation before the court order is issued, so the set has to be redone.",
      "Missing heirs who must also sign, discovered only after the file reaches the institution.",
      "Documents issued so early that they age out of the receiving institution's validity window.",
    ],
    compare: [
      {
        option: "Direct submission with certified copies",
        useWhen: "The asset holder accepts heir evidence without a court appointment",
        time: "1–3 weeks",
        note: "Fastest route; confirm the requirement in writing first",
      },
      {
        option: "Court-appointed administrator",
        useWhen: "The institution, land registry or share registrar requires a formal appointment",
        time: "Several weeks or more",
        note: "Plan translation and legalisation around the order date",
      },
      {
        option: "Power of attorney to a local representative",
        useWhen: "The heirs cannot travel to the country holding the assets",
        time: "1–2 weeks for the document chain",
        note: "Wording must match the institution's template",
      },
    ],
    faqs: [
      {
        q: "Does a Thai death certificate work abroad on its own?",
        a: "Rarely. Foreign banks and registries want a certified copy with a certified translation, legalised through the consular chain, and often an embassy certification on top. They also want the relationship proven, so the birth or marriage certificate normally travels with it in the same file.",
      },
      {
        q: "Do we always need a court-appointed administrator?",
        a: "No. Some institutions release assets on heir evidence alone, particularly for smaller balances, while land registries and share registrars usually insist on a formal appointment. Because the court step dominates the timeline, we always confirm the requirement in writing before filing anything.",
      },
      {
        q: "Can one heir act for the others?",
        a: "Yes, with a power of attorney signed by each heir before a Notarial Services Attorney and worded to name the specific asset or account. General authorisations are frequently refused by banks, so we draft against the institution's own template whenever one exists.",
      },
      {
        q: "What if the will is written in another language?",
        a: "It is translated and certified in the same chain as the rest of the file. Where the will was executed abroad, the foreign grant or probate document is also legalised for use in Thailand, and we check early whether the Thai institution wants the grant, the will or both.",
      },
      {
        q: "How long does the whole process take?",
        a: "Two to eight weeks is realistic when no court appointment is required, and considerably longer when one is, because the court's calendar sets the pace. Document preparation itself is rarely the bottleneck once the requirement list is settled.",
      },
      {
        q: "Can you handle this while the family is overseas?",
        a: "Yes. We order the Thai certificates, prepare and notarise the powers of attorney in the wording the institution needs, translate and legalise the set, and courier it. Estates are stressful and time-sensitive; after more than 15 years we sequence the court, bank and consular steps in parallel wherever the rules allow.",
      },
    ],
    related: [
      ["Notary Public service", "/en/notary"],
      ["Consular legalisation", "/en/mfa-legalisation"],
      ["Certified translation", "/en/services/translation"],
      CASES_LINK,
    ],
    citations: [DOPA_CITE, COURT_CITE, MFA_CITE],
  },

  {
    slug: "financial-documents-visa-application",
    cluster: "visa",
    title: "Financial evidence for a visa application",
    metaTitle: "Case study: financial documents for a visa application",
    metaDescription:
      "Bank statements, sponsorship letters, tax records and employment proof prepared for a visa file — what consulates actually check, the certification order, and the errors that cause refusals.",
    h1: "Case study: building financial evidence a consulate will accept without a follow-up request",
    answer:
      "Consulates assess financial evidence on three questions: is the money genuinely available, is its origin explainable, and does it belong to the person named on the application. A file answers those with an original bank statement or bank letter covering the required period, income evidence such as payslips or tax records, and a sponsorship declaration where someone else is funding the trip. Certified translations are attached where the document is not in the language the mission accepts.",
    audience:
      "Applicants for tourist, student, spouse or long-stay visas who must prove funds, and sponsors supporting a family member's application.",
    facts: [
      { label: "Common statement period", value: "Three to six months, set by the individual mission" },
      { label: "Format preferred", value: "Bank-issued statement or letter, stamped and signed by the bank" },
      { label: "Income evidence", value: "Payslips, employment letter, or tax filings from the Revenue Department" },
      { label: "Sponsor documents", value: "Declaration of support plus the sponsor's own financial and identity documents" },
      { label: "Typical preparation time", value: "3–10 working days including translation and certification" },
    ],
    documents: [
      "Bank statement covering the period the mission specifies, issued and stamped by the bank",
      "Bank letter confirming the account holder, account number and current balance",
      "Payslips for recent months and an employment letter stating position, start date and salary",
      "Tax filing or income certificate where the mission asks for it",
      "Sponsorship declaration, signed and, where required, notarised",
      "Sponsor's identity document, proof of relationship and their own financial evidence",
      "Certified translations of any document not in the mission's accepted language",
    ],
    steps: [
      {
        title: "Read the mission's own list before collecting anything",
        detail:
          "Requirements differ by country and visa category, so the checklist is taken from the mission handling the application rather than from a general template.",
        who: "Advisory team",
        eta: "Same day",
      },
      {
        title: "Order documents in the bank's official format",
        detail:
          "A stamped statement or bank letter is requested rather than a printed online summary, because unstamped printouts are the most common cause of a follow-up request.",
        who: "Applicant's bank",
        eta: "1–5 working days",
      },
      {
        title: "Explain any unusual movement",
        detail:
          "Large recent deposits are documented with their source — a property sale, bonus or family transfer — in a short covering note rather than left for the officer to interpret.",
        who: "Applicant with advisory support",
        eta: "1–2 working days",
      },
      {
        title: "Prepare the sponsorship layer where funds are not the applicant's own",
        detail:
          "The declaration is drafted to name the applicant, the relationship and the trip, notarised where the mission requires a certified signature.",
        who: "Sponsor and Notarial Services Attorney",
        eta: "1–3 working days",
      },
      {
        title: "Translate, certify and assemble in the mission's order",
        detail:
          "Translations are certified and the bundle is arranged to follow the mission's checklist sequence so the officer can verify each item quickly.",
        who: "Translation team",
        eta: "2–4 working days",
      },
    ],
    pitfalls: [
      "An online printout with no bank stamp or signature.",
      "A large deposit made days before the application with no explanation attached.",
      "A statement period shorter than the mission requires.",
      "A sponsorship letter that does not name the applicant, the relationship or the purpose of the trip.",
      "Account holder name spelled differently from the passport.",
      "Translating the statement but not the bank letter, leaving half the evidence unreadable to the officer.",
    ],
    compare: [
      {
        option: "Applicant's own funds",
        useWhen: "The applicant holds sufficient balance and income evidence",
        time: "3–7 working days to prepare",
        note: "Simplest file; history matters as much as the balance",
      },
      {
        option: "Sponsored application",
        useWhen: "A family member or employer funds the trip",
        time: "5–10 working days",
        note: "Sponsor's documents are assessed as strictly as the applicant's",
      },
      {
        option: "Employer-funded business travel",
        useWhen: "A company covers the cost of the trip",
        time: "3–7 working days",
        note: "Company letter plus corporate evidence usually replaces personal statements",
      },
    ],
    faqs: [
      {
        q: "How much money should be in the account?",
        a: "There is no single figure; missions look at whether the balance plausibly covers the trip and any stated purpose, and whether the money has been there long enough to be genuinely available. A modest but stable balance with regular income is generally read more favourably than a large sum deposited a week before applying.",
      },
      {
        q: "Is an online bank printout acceptable?",
        a: "Often not. Most missions want a statement or letter issued by the bank with its stamp and signature, because an unstamped printout cannot be verified. Requesting the official format at the branch costs a few days and removes one of the most common reasons for a document request.",
      },
      {
        q: "Do bank statements need to be translated?",
        a: "If they are not in a language the mission accepts, yes, and the translation must be certified. We translate the full document including the bank's header, stamp text and any footnotes, since a partial translation leaves the officer unable to confirm the source.",
      },
      {
        q: "Can a family member sponsor me?",
        a: "Yes, in most categories. The sponsor signs a declaration naming you, the relationship and the trip, and supplies their own identity and financial evidence. Some missions require the signature to be certified before a notary, which we confirm before the document is drafted.",
      },
      {
        q: "A large transfer just arrived in my account. Is that a problem?",
        a: "Only if it is unexplained. Attach the evidence of its source — a sale contract, bonus slip or family transfer record — as part of the file. Officers are trained to notice sudden deposits, so explaining it in advance is far stronger than answering a query later.",
      },
      {
        q: "Can you prepare the whole financial bundle?",
        a: "Yes. We work from the specific mission's checklist, tell you exactly which format to request from your bank, draft and notarise the sponsorship layer, certify the translations and assemble the bundle in the officer's reading order. Over 15 years the pattern is consistent: refusals usually come from presentation, not from the amount.",
      },
    ],
    related: [
      ["Visa services", "/en/visa"],
      ["Certified translation", "/en/services/translation"],
      ["Notary Public service", "/en/notary"],
      CASES_LINK,
    ],
    citations: [IMMI_CITE, RD_CITE],
  },

  {
    slug: "ltr-visa-document-set",
    cluster: "ltr",
    title: "Long-Term Resident visa document set",
    metaTitle: "Case study: Thailand Long-Term Resident visa documents",
    metaDescription:
      "The document set behind an LTR application — category choice, income and asset evidence, insurance, employment proof, translations and certification, plus the errors that stall an endorsement.",
    h1: "Case study: assembling a Thailand Long-Term Resident visa file",
    answer:
      "The Long-Term Resident visa is administered through the Board of Investment, which endorses a qualified applicant before the visa itself is issued. The file turns on the category chosen — wealthy global citizen, wealthy pensioner, work-from-Thailand professional or highly skilled professional — because each has its own income, asset, insurance and employment evidence. Foreign documents are translated and certified, and the endorsement stage usually takes longer than the visa stage.",
    audience:
      "Remote professionals, retirees, senior specialists and investors evaluating a long-stay route into Thailand and preparing the supporting evidence.",
    facts: [
      { label: "Administering authority", value: "Thailand Board of Investment (BOI)" },
      { label: "Categories", value: "Wealthy global citizen, wealthy pensioner, work-from-Thailand professional, highly skilled professional" },
      { label: "Two-stage process", value: "Qualification endorsement first, then visa issuance" },
      { label: "Health insurance", value: "Coverage evidence in the form and amount the programme specifies" },
      { label: "Typical duration", value: "Several weeks to a few months, driven mainly by the endorsement review" },
    ],
    documents: [
      "Passport with sufficient validity and any current Thai visa pages",
      "Evidence of personal income over the period the category requires — payslips, tax filings or pension statements",
      "Asset evidence where the category relies on it, such as investment or property records",
      "Employment contract or engagement letter with the foreign employer, and the employer's corporate records",
      "Health insurance policy or an equivalent form of coverage evidence",
      "Degree certificates and professional qualifications for the skilled-professional categories",
      "Marriage and birth certificates for dependants included in the application",
      "Certified translations of every foreign-language document in the bundle",
    ],
    steps: [
      {
        title: "Choose the category on evidence, not on preference",
        detail:
          "We test the documents you can actually produce against each category's criteria before an application is opened, because a category change mid-review restarts the assessment.",
        who: "Advisory team",
        eta: "1–5 working days",
      },
      {
        title: "Build the income and asset record",
        detail:
          "Income is evidenced across the full required period with consistent figures between payslips, bank credits and tax filings.",
        who: "Applicant with advisory support",
        eta: "1–3 weeks depending on the source countries",
      },
      {
        title: "Verify the employer and the engagement",
        detail:
          "For the work-from-Thailand and skilled categories, the foreign employer's corporate documents and the contract terms are checked against the programme's conditions.",
        who: "Employer",
        eta: "1–2 weeks",
      },
      {
        title: "Translate and certify the foreign documents",
        detail:
          "Degrees, contracts, tax records and insurance documents are translated and certified with a single spelling convention anchored to the passport.",
        who: "Translation team",
        eta: "3–7 working days",
      },
      {
        title: "File for endorsement, then for the visa",
        detail:
          "The qualification endorsement is submitted first; once granted, the visa is issued and the accompanying work authorisation is arranged where the category provides for it.",
        who: "BOI and the issuing office",
        eta: "Several weeks to a few months",
      },
    ],
    pitfalls: [
      "Choosing a category the evidence does not support and having to refile.",
      "Income figures that do not reconcile between payslips, bank statements and tax filings.",
      "Insurance coverage that falls short of the form or amount the programme specifies.",
      "Dependants added late, after the main file has already been assessed.",
      "Untranslated foreign tax documents, which are among the most closely read items.",
      "Treating the endorsement and the visa as one step and planning travel around the wrong date.",
    ],
    compare: [
      {
        option: "LTR visa",
        useWhen: "Long-horizon stay with qualifying income, assets or employment",
        time: "Weeks to months",
        note: "Heaviest evidence burden, longest stay benefit",
      },
      {
        option: "Non-B visa with work permit",
        useWhen: "Employment by a Thai company",
        time: "2–6 weeks",
        note: "Employer-driven; renewals are annual",
      },
      {
        option: "Retirement visa",
        useWhen: "Age and financial criteria are met and no work is intended",
        time: "1–4 weeks",
        note: "Simpler file, but no work authorisation",
      },
    ],
    faqs: [
      {
        q: "Which LTR category should I apply under?",
        a: "The category is decided by the evidence you can produce, not by which label fits best. A remote professional with a foreign employment contract normally falls under work-from-Thailand, while a pensioner relies on pension and asset records. We test the documents against each category's criteria first, because switching mid-review restarts the assessment.",
      },
      {
        q: "How long does an LTR application take?",
        a: "Plan for several weeks to a few months. The qualification endorsement is the long stage because it is a substantive review of income, employment and insurance evidence; the visa issuance afterwards is comparatively quick. Travel and lease commitments should be planned around the endorsement date rather than the application date.",
      },
      {
        q: "Do I need Thai translations of my foreign documents?",
        a: "Foreign-language documents are submitted with certified translations, and tax filings, employment contracts and insurance policies get the closest reading. We keep one transliteration of your name across the whole bundle, anchored to the passport, because inconsistent spelling is a frequent cause of clarification requests.",
      },
      {
        q: "Can my family be included?",
        a: "Dependants can be included, supported by marriage and birth certificates that are translated and certified in the same chain as the main file. Adding them at the start is materially faster than filing separately once the main application has already been assessed.",
      },
      {
        q: "Does the LTR visa allow me to work?",
        a: "Certain categories provide for work authorisation in Thailand alongside the visa, while others are designed for people whose income comes from abroad. Because the answer depends on the category, we confirm the work position before you resign from or restructure an existing role.",
      },
      {
        q: "Can you manage the whole application?",
        a: "Yes. We assess which category your evidence actually supports, list the documents by source country, translate and certify the bundle, coordinate the employer and insurance evidence, and manage the endorsement and visa stages. With more than 15 years advising on Thai document files, our value here is the category decision at the start — that is what determines whether the file clears.",
      },
    ],
    related: [
      ["LTR visa service", "/en/ltr"],
      ["Visa services", "/en/visa"],
      ["Certified translation", "/en/services/translation"],
      CASES_LINK,
    ],
    citations: [BOI_CITE, IMMI_CITE],
  },
];
